import React, { useCallback, useContext } from 'react'

import { pushWithRegion } from 'actions/NavigationActions'

import { GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import useExperienceSearchFallback from 'hooks/Experiences/useExperienceSearchFallback'

import GlobalSearchTrackingContext from 'contexts/GlobalSearch/GlobalSearchTracking'
import { OFFER_TYPE_EXPERIENCE } from 'constants/offer'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { trackEvent } from 'analytics/analytics'
import { searchEventWithContext } from 'analytics/snowplow/events'
import { mapGlobalSearchContextToSnowplowSearchEvent } from 'analytics/mapSnowplowSearchTracking'
import ExperiencesSearchInputs from './ExperiencesSearchInputs'
import { type QueryStringSearchParams, replaceExperienceQueryString } from 'lib/experiences/experienceUtils'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'

interface Props {
  onSearch?: (searchItem?: App.SearchItem) => void
  isMapView?: boolean
  className?: string
}

function ExperiencesSearchControls(props: Props) {
  const {
    onSearch,
    isMapView = false,
    className,
  } = props
  const onSearchEvent = useContext(GlobalSearchTrackingContext)

  const dispatch = useAppDispatch()

  const { searchItem, checkinDate, checkoutDate, dateSearchOptionId, experienceCategories } = useContext(GlobalSearchStateContext)

  const fallbackItem = useExperienceSearchFallback()
  const toBeSearchedItem = searchItem ?? fallbackItem
  const fetchingExperience = useAppSelector(state => state.experience.fetchingExperiences[toBeSearchedItem.value])

  const handleSearchButtonClick = useCallback((e?: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
    e?.preventDefault()

    const formattedCheckinDate = checkinDate?.format(ISO_DATE_FORMAT)
    const effectiveCheckout = checkoutDate ?? checkinDate
    const formattedCheckoutDate = effectiveCheckout?.format(ISO_DATE_FORMAT)

    const query: QueryStringSearchParams = {
      checkIn: formattedCheckinDate,
      checkOut: formattedCheckoutDate,
    }

    if (experienceCategories?.length) {
      query.categories = experienceCategories.join(',')
    }

    if (toBeSearchedItem.searchType === 'experience') {
      query.experienceId = toBeSearchedItem.value
      query.experienceName = toBeSearchedItem.format.mainText
    } else {
      query.destinationId = toBeSearchedItem.value
      query.destinationName = toBeSearchedItem.format.mainText
    }

    const queryString = replaceExperienceQueryString(query)

    trackEvent(searchEventWithContext({
      searchEvent: { type: 'search_bar' },
      sortContext: { value: 'recommended' },
      searchContext: mapGlobalSearchContextToSnowplowSearchEvent({
        searchItem,
        checkinDate,
        checkoutDate: effectiveCheckout,
        dateSearchOptionId,
        searchVerticals: new Set(['experiences']),
      },
      ),
    }))

    const path = isMapView ? '/search/experiences/map' : '/search/experiences'

    dispatch(pushWithRegion(`${path}?${queryString}`))

    onSearchEvent('submit-button', {
      searchItem,
      vertical: OFFER_TYPE_EXPERIENCE,
      checkIn: formattedCheckinDate,
      checkOut: formattedCheckoutDate,
    })

    onSearch?.(searchItem)
  }, [onSearch, searchItem, checkinDate, checkoutDate, dateSearchOptionId, experienceCategories, isMapView, dispatch, onSearchEvent, toBeSearchedItem])

  return <div className={className}>
    <ExperiencesSearchInputs
      onSearchButtonClick={handleSearchButtonClick}
      disableSearch={fetchingExperience}
      isMapView={isMapView}
    />
  </div>
}

export default ExperiencesSearchControls
